import type { Scene } from '@src/editor/models/layout/Scene';
import type { AnchorType } from '@src/editor/models/layout/types';

export interface ElementTiming {
  elementId: string;
  start: number;
  end: number;
}

export interface SceneRange {
  start: number;
  end: number;
}

/**
 * Minimal interface for editor model elements (from tracksMap).
 */
export interface EditorElement {
  id: string;
  time: number;
  durationSeconds: number;
  type?: string;
}

/**
 * Extracts ElementTiming[] from editor model elements using a predicate function.
 * This variant is useful when you need to call a method like `isOriginalVideoElement(el.toJSON())`.
 * Returns sorted by start time.
 *
 * Optimization: Filters by type first to avoid expensive toJSON() + Zod parsing for non-video elements.
 */
export function extractTimingsFromEditorElements<T extends EditorElement>(
  elements: T[],
  isOriginalVideo: (element: T) => boolean,
): ElementTiming[] {
  return elements
    .filter(isOriginalVideo)
    .map(el => ({
      elementId: el.id,
      start: el.time,
      end: el.time + el.durationSeconds,
    }))
    .sort((a, b) => a.start - b.start);
}

/**
 * Builds an ElementTiming map from an array of element timings.
 * This is a convenience utility to convert the sorted timings array
 * into the Map format required by resolveSceneRange and resolveAnchorTime.
 */
export function buildElementTimingMap(
  timings: ElementTiming[],
): Map<string, ElementTiming> {
  return new Map(timings.map(t => [t.elementId, t]));
}

export type SceneBoundaryKind = 'start' | 'end';

export interface SceneBoundaryAnchors {
  current: AnchorType;
  adjacent: AnchorType;
}

const TIME_EPSILON = 1 / 1000;

const clipStartAnchor: AnchorType = 'clip-start';
const clipEndAnchor: AnchorType = 'clip-end';

export function resolveAnchorTime(
  anchor: AnchorType,
  elementTimings: Map<string, ElementTiming>,
  timelineDuration: number,
): number {
  if (anchor === clipStartAnchor) {
    return 0;
  }

  if (anchor === clipEndAnchor) {
    return timelineDuration;
  }

  if (typeof anchor === 'object' && 'elementId' in anchor) {
    const timing = anchor.elementId
      ? elementTimings.get(anchor.elementId)
      : undefined;

    if (!timing) {
      return 0;
    }

    if (anchor.end) {
      return timing.end;
    }

    const relativeTime = anchor.relativeTime ?? 0;
    return timing.start + relativeTime;
  }

  return 0;
}

export function resolveSceneRange(
  scene: Scene,
  elementTimings: Map<string, ElementTiming>,
  timelineDuration: number,
): SceneRange {
  const start = resolveAnchorTime(
    scene.anchorStart,
    elementTimings,
    timelineDuration,
  );
  const rawEnd = resolveAnchorTime(
    scene.anchorEnd,
    elementTimings,
    timelineDuration,
  );
  const end = Math.max(rawEnd, start);

  return { start, end };
}

export function resolveSceneBoundaryAnchors(
  boundaryTime: number,
  elementTimings: ElementTiming[],
  kind: SceneBoundaryKind,
): SceneBoundaryAnchors | null {
  if (!elementTimings.length) {
    return null;
  }

  const sorted = elementTimings.slice().sort((a, b) => a.start - b.start);

  const index = sorted.findIndex(
    timing =>
      boundaryTime >= timing.start - TIME_EPSILON &&
      boundaryTime <= timing.end + TIME_EPSILON,
  );

  if (index === -1) {
    return null;
  }

  const timing = sorted[index];
  const atStart = Math.abs(boundaryTime - timing.start) <= TIME_EPSILON;
  const atEnd = Math.abs(boundaryTime - timing.end) <= TIME_EPSILON;

  if (atStart && index > 0) {
    const previous = sorted[index - 1];
    if (kind === 'start') {
      return {
        current: {
          elementId: timing.elementId,
          end: false,
          relativeTime: null,
        },
        adjacent: {
          elementId: previous.elementId,
          end: true,
          relativeTime: null,
        },
      };
    }

    return {
      current: {
        elementId: previous.elementId,
        end: true,
        relativeTime: null,
      },
      adjacent: {
        elementId: timing.elementId,
        end: false,
        relativeTime: null,
      },
    };
  }

  if (atEnd && index < sorted.length - 1) {
    const next = sorted[index + 1];
    if (kind === 'start') {
      return {
        current: {
          elementId: next.elementId,
          end: false,
          relativeTime: null,
        },
        adjacent: {
          elementId: timing.elementId,
          end: true,
          relativeTime: null,
        },
      };
    }

    return {
      current: {
        elementId: timing.elementId,
        end: true,
        relativeTime: null,
      },
      adjacent: {
        elementId: next.elementId,
        end: false,
        relativeTime: null,
      },
    };
  }

  const clampedTime = Math.min(
    Math.max(boundaryTime, timing.start),
    timing.end,
  );
  const relativeTime = clampedTime - timing.start;

  const anchor: AnchorType = {
    elementId: timing.elementId,
    end: false,
    relativeTime,
  };

  if (kind === 'start') {
    return {
      current: anchor,
      adjacent: anchor,
    };
  }

  return {
    current: anchor,
    adjacent: anchor,
  };
}
